import { EntityModel } from 'src/core/services/data/models';
import { DataService, property } from 'src/core/services/data.service';
import { BookemZulu as Model, OData, $data } from 'src/core/services/data/JayDataContext';
import { computed } from 'mobx';
import { FormatService } from 'src/core/services/format.service';
import { StatusTexts } from "src/app/shared/status-texts";

export class QuoteListDataService extends DataService<typeof Model.Bookings.Logic.ViewModels.QuoteSummary, Model.Bookings.Logic.ViewModels.QuoteSummary, QuoteSummary> {
	constructor(contextService: any, entitySet: (context: OData.Container) => $data.EntitySet<typeof Model.Bookings.Logic.ViewModels.QuoteSummary, Model.Bookings.Logic.ViewModels.QuoteSummary>, private formatService: FormatService) {
		super(contextService, entitySet);
	}

	protected map(entity: Model.Bookings.Logic.ViewModels.QuoteSummary): QuoteSummary {
		return new QuoteSummaryModel(entity, this.formatService);
	}

	public static make(ctx: any, formatService: FormatService): QuoteListDataService {
		return new QuoteListDataService(ctx, ctx => ctx.QuoteSummaries, formatService);
	}
}

export class QuoteSummaryModel extends EntityModel<Model.Bookings.Logic.ViewModels.QuoteSummary, QuoteSummary> implements QuoteSummary {
	@property<QuoteSummaryModel>(o => o.entity.id, (o, value) => o.entity.id = value)
	public id: number;
	@property<QuoteSummaryModel>(o => o.entity.parentQuoteId, (o, value) => o.entity.parentQuoteId = value)
	public parentQuoteId: number;
	@property<QuoteSummaryModel>(o => o.entity.quoteNumber, (o, value) => o.entity.quoteNumber = value)
	public quoteNumber: string;

	@property<QuoteSummaryModel>(o => o.entity.languageCode, (o, value) => o.entity.languageCode = value)
	public languageCode: string;

	@property<QuoteSummaryModel>(o => o.entity.brandName, (o, value) => o.entity.brandName = value)
	public brandName: string;
	@property<QuoteSummaryModel>(o => o.entity.brandId, (o, value) => o.entity.brandId = value)
	public brandId: number;

	@property<QuoteSummaryModel>(o => o.entity.agentName, (o, value) => o.entity.agentName = value)
	public agentName: string;
	@property<QuoteSummaryModel>(o => o.entity.agentId, (o, value) => o.entity.agentId = value)
	public agentId?: number;

	@property<QuoteSummaryModel>(o => o.entity.clientDisplayName, (o, value) => o.entity.clientDisplayName = value)
	public clientDisplayName: string;
	@property<QuoteSummaryModel>(o => o.entity.clientId, (o, value) => o.entity.clientId = value)
	public clientId: number;

	@property<QuoteSummaryModel>(o => o.entity.marketName, (o, value) => o.entity.marketName = value)
	public marketName: string;

	@property<QuoteSummaryModel>(o => o.entity.marketCurrencyIso3Name, (o, value) => o.entity.marketCurrencyIso3Name = value)
	public marketCurrencyIso3Name: string;
	@property<QuoteSummaryModel>(o => o.entity.marketId, (o, value) => o.entity.marketId = value)
	public marketId: number;

	@property<QuoteSummaryModel>(o => o.entity.managedById, (o, value) => o.entity.managedById = value)
	public managedById: number;
	@property<QuoteSummaryModel>(o => o.entity.managedByUserName, (o, value) => o.entity.managedByUserName = value)
	public managedByUserName: string;

	@property<QuoteSummaryModel>(o => o.entity.hasChildren, (o, value) => o.entity.hasChildren = value)
	public hasChildren: boolean;
	@property<QuoteSummaryModel>(o => o.entity.hasAgents, (o, value) => o.entity.hasAgents = value)
	public hasAgents: boolean;
	@property<QuoteSummaryModel>(o => o.entity.hasEntries, (o, value) => o.entity.hasEntries = value)
	public hasEntries: boolean;
	@property<QuoteSummaryModel>(o => o.entity.hasPaxs, (o, value) => o.entity.hasPaxs = value)
	public hasPaxs: boolean;

	@property<QuoteSummaryModel>(o => o.entity.travelRegionCode, (o, value) => o.entity.travelRegionCode = value)
	public travelRegionCode: string;
	@property<QuoteSummaryModel>(o => o.entity.travelRegionId, (o, value) => o.entity.travelRegionId = value)
	public travelRegionId: number;

	@property<QuoteSummaryModel>(o => o.entity.status, (o, value) => o.entity.status = value)
	public status: number;

	@computed
	public get statusText(): string {
		return StatusTexts.getQuoteStatusFromNumber(this.entity.status);
	}

	@property<QuoteSummaryModel>(o => o.entity.dateFrom, (o, value) => o.entity.dateFrom = value)
	public dateFrom: Date;
	@computed
	public get dateFromText(): string {
		return this.dateFrom == null ? null : this.formatService.toString('short-date', this.dateFrom, 'utc');
	}

	@property<QuoteSummaryModel>(o => o.entity.dateTo, (o, value) => o.entity.dateTo = value)
	public dateTo: Date;
	@computed
	public get dateToText(): string {
		return this.dateTo == null ? null : this.formatService.toString('short-date', this.dateTo, 'utc');
	}

	@property<QuoteSummaryModel>(o => o.entity.reservationDate, (o, value) => o.entity.reservationDate = value)
	public reservationDate: Date;
	@computed
	public get reservationDateText(): string {
		return this.reservationDate == null ? null : this.formatService.toString('short-date', this.reservationDate, 'utc');
	}

	@property<QuoteSummaryModel>(o => o.entity.bookingDate, (o, value) => o.entity.bookingDate = value)
	public bookingDate?: Date;

	@property<QuoteSummaryModel>(o => o.entity.confirmedDate, (o, value) => o.entity.confirmedDate = value)
	public confirmedDate?: Date;

	@property<QuoteSummaryModel>(o => o.entity.paid, (o, value) => o.entity.paid = value)
	public paid: number;

	@property<QuoteSummaryModel>(o => o.entity.totalCost, (o, value) => o.entity.totalCost = value)
	public totalCost: number;
	@property<QuoteSummaryModel>(o => o.entity.totalSale, (o, value) => o.entity.totalSale = value)
	public totalSale: number;
	@property<QuoteSummaryModel>(o => o.entity.totalSaleGross, (o, value) => o.entity.totalSaleGross = value)
	public totalSaleGross: number;
	@property<QuoteSummaryModel>(o => o.entity.totalSalesmanProfit, (o, value) => o.entity.totalSalesmanProfit = value)
	public totalSalesmanProfit: number;

	constructor(entity: Model.Bookings.Logic.ViewModels.QuoteSummary, private formatService: FormatService) {
		super(entity);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/quotes/list/quote-list.data.ts